import React, { useState, useCallback, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';

const ImageCropper = ({ defaultImage, setCroppedImage,croppedImage,setOriginalFileName  }) => {
    const fileInputRef = useRef(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [showCropper, setShowCropper] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(defaultImage);
    // const [croppedImage, setCroppedImageState] = useState(null);
    const [containerHeight, setContainerHeight] = useState('auto');
    const [selectedFileName, setSelectedFileName] = useState("");
    // Image selection handler
    const onSelectFile = (e) => {
        const file = e.target.files[0];
        if (file) {
            const originalFileName = file.name;
            console.log("Original Filename:", originalFileName);

            setSelectedFileName(originalFileName);

            if (setOriginalFileName) {
                setOriginalFileName(originalFileName);
            }
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageToCrop(reader.result);
                setShowCropper(true);
            };
            reader.readAsDataURL(file);
        }
    };

    // Trigger file input
    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    // Crop complete callback
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Crop image handler
    const handleCrop = async () => {
        if (!imageToCrop || !croppedAreaPixels) return;

        const croppedImage = await getCroppedImg(imageToCrop, croppedAreaPixels);
        // setCroppedImageState(croppedImage);
        setCroppedImage(croppedImage);
        setShowCropper(false);
    };

    // Get cropped image from canvas
    const getCroppedImg = (imageSrc, croppedAreaPixels) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const image = new Image();
        image.src = imageSrc;

        return new Promise((resolve, reject) => {
            image.onload = () => {
                const { x, y, width: cropWidth, height: cropHeight } = croppedAreaPixels;
                
                // Set canvas size to 400x400
                canvas.width = 400;
                canvas.height = 400;
                
                // Draw and scale the cropped image to 400x400
                ctx.drawImage(
                    image,
                    x, y, cropWidth, cropHeight,  // Source rectangle
                    0, 0, 400, 400                // Destination rectangle (400x400)
                );

                const croppedImageUrl = canvas.toDataURL('image/png', 0.8);
                resolve(croppedImageUrl);
            };
            image.onerror = reject;
        });
    };

    // Adjust container height to ensure full visibility
    useEffect(() => {
        const calculateOptimalHeight = () => {
            // Determine optimal height based on window size
            const windowHeight = window.innerHeight;
            const maxHeight = windowHeight * 0.9; // 90% of window height
            const idealHeight = Math.min(maxHeight, 700); // Cap at 700px
            setContainerHeight(idealHeight);
        };

        // Calculate on mount and resize
        calculateOptimalHeight();
        window.addEventListener('resize', calculateOptimalHeight);

        // Cleanup listener
        return () => window.removeEventListener('resize', calculateOptimalHeight);
    }, []);

    return (
        <>
            {showCropper && (
                <div className="my-cropper-overlay">
                    <div 
                        className="my-cropper-container" 
                        style={{ height: containerHeight }}
                    >
                        <div className="cropper-header">
                            <h2>Crop Your Image</h2>
                            <button 
                                className="close-button" 
                                onClick={() => setShowCropper(false)}
                                aria-label="Close cropper"
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div className="cropper-content">
                            <div className="cropper-area">
                                <Cropper
                                    image={imageToCrop}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    onCropChange={setCrop}
                                    onCropComplete={onCropComplete}
                                    onZoomChange={setZoom}
                                    showGrid={true}
                                />
                            </div>

                            <div className="cropper-controls">
                                <div className="zoom-control">
                                    <label htmlFor="zoom-range">Zoom</label>
                                    <input
                                        type="range"
                                        id="zoom-range"
                                        min={1}
                                        max={3}
                                        step={0.1}
                                        value={zoom}
                                        onChange={(e) => setZoom(Number(e.target.value))}
                                        className="zoom-slider"
                                    />
                                </div>
                                
                                <div className="action-buttons">
                                    <button 
                                        onClick={handleCrop} 
                                        className="crop-button"
                                        type='button'
                                    >
                                        Apply Crop
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="cropper-container">
                <div 
                    className="img-container" 
                    onClick={triggerFileInput}
                >
                    <img
                        src={ croppedImage || defaultImage ||  imageToCrop }
                        alt="Preview"
                        className="preview-image"
                    />
                    {/* <div className="edit-overlay">
                        <span className="edit-text">Edit</span>
                    </div> */}
                </div>
                <input 
                    type="file" 
                    style={{display:'none'}}
                    ref={fileInputRef}
                    className="file-input" 
                    onChange={onSelectFile} 
                    accept="image/*" 
                    aria-label="Select image to crop"
                />
            </div>

            <style jsx>{`
                /* Overlay and Modal Styles */
                .my-cropper-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-color: rgba(0, 0, 0, 0.7);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                    padding: 20px;
                    box-sizing: border-box;
                    animation: fadeIn 0.3s ease-out;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .my-cropper-container {
                    background-color: white;
                    border-radius: 16px;
                    width: 100%;
                    max-width: 650px;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    box-shadow: 0 15px 50px rgba(0, 0, 0, 0.2);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                }

                /* Header Styles */
                .cropper-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 15px 20px;
                    border-bottom: 1px solid #e8e8e8;
                    background-color: #f9f9f9;
                    flex-shrink: 0;
                }

                .cropper-header h2 {
                    margin: 0;
                    font-size: 18px;
                    color: #333;
                    font-weight: 600;
                }

                .close-button {
                    background: none;
                    border: none;
                    font-size: 24px;
                    color: #888;
                    cursor: pointer;
                    transition: color 0.2s ease;
                    padding: 0;
                    line-height: 1;
                }

                .close-button:hover {
                    color: #000;
                }

                /* Cropper Content Styles */
                .cropper-content {
                    display: flex;
                    flex-direction: column;
                    flex-grow: 1;
                    overflow: hidden;
                }

                .cropper-area {
                    position: relative;
                    flex-grow: 1;
                    min-height: 300px;
                    background: #f4f4f4;
                    overflow: hidden;
                }

                .cropper-controls {
                    padding: 15px 20px;
                    border-top: 1px solid #e8e8e8;
                    background-color: #f9f9f9;
                    flex-shrink: 0;
                }

                .zoom-control {
                    display: flex;
                    align-items: center;
                    margin-bottom: 15px;
                }

                .zoom-control label {
                    margin-right: 15px;
                    font-size: 14px;
                    color: #666;
                    min-width: 50px;
                }

                .zoom-slider {
                    flex-grow: 1;
                    appearance: none;
                    width: 100%;
                    height: 8px;
                    background: #e0e0e0;
                    outline: none;
                    opacity: 0.7;
                    transition: opacity 0.2s;
                    border-radius: 4px;
                }

                .zoom-slider::-webkit-slider-thumb {
                    appearance: none;
                    width: 18px;
                    height: 18px;
                    background: #4CAF50;
                    cursor: pointer;
                    border-radius: 50%;
                    box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.2);
                }

                .action-buttons {
                    display: flex;
                    justify-content: flex-end;
                }

                .crop-button {
                    background-color: #4CAF50;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                }

                .crop-button:hover {
                    background-color: #45a049;
                    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
                    transform: translateY(-2px);
                }

                /* Image Container Styles */
                .img-container {
                    position: relative;
                    width: 10rem;
                    height: 10rem;
                    cursor: pointer;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                }

                .preview-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .edit-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-color: rgba(0, 0, 0, 0.4);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }

                .img-container:hover .edit-overlay {
                    opacity: 1;
                }

                .edit-text {
                    color: white;
                    font-weight: 600;
                    font-size: 14px;
                }

                .file-input {
                    display: none;
                }

                /* Responsive Adjustments */
                @media (max-width: 600px) {
                    .my-cropper-container {
                        width: 95%;
                        margin: 0 10px;
                        max-height: 95vh;
                    }

                    .cropper-area {
                        min-height: 250px;
                    }

                    .cropper-header, .cropper-controls {
                        padding: 12px 15px;
                    }
                }
            `}</style>
        </>
    );
};

export default ImageCropper;